/**
 * Strict wire parsing for the synthetic 06/08 management chain.
 * SYNTHETIC DATA / NON-OPERATIONAL.
 *
 * WHY THIS IS STRICT AND NOT DEFENSIVE. Every parser here REJECTS a payload that carries a key it
 * does not expect, rather than ignoring it. That is the same rule the existing read-model wire
 * parsers in this repository follow, and it is chosen for the same reason: a server that starts
 * sending a field the client silently drops is a contract change nobody noticed, and the failure
 * surfaces much later as a wrong value rather than as a loud error.
 *
 * The cost is real and is accepted deliberately: adding a field to the server response breaks this
 * client until the parser is updated. Both sides are in this repository and change together, so the
 * cost is a compile-and-test cycle rather than an outage.
 *
 * WHAT THESE PARSERS DO NOT DO. They do not interpret. A `state` of `PUBLISHED` is passed through as
 * `PUBLISHED`; nothing here decides what a user may see, and nothing derives a display status. That
 * judgement belongs to the presentation layer, and a wire parser that made it would be a second
 * place for the three-facts rule to be wrong.
 */
import type {
  ClaimsView,
  ExactReadbackView,
  ProductModelView,
  PublicationView,
  ReviewCaseView,
  SubmissionView,
} from './product-review-port';

function record(value: unknown, what: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error(`Invalid ${what}: expected an object`);
  }
  return value as Record<string, unknown>;
}

/**
 * Requires EXACTLY these keys.
 *
 * A missing key and an unexpected key are both failures, and they are reported differently so a
 * reader can tell "the server stopped sending it" from "the server started sending something new".
 */
function exactKeys(value: Record<string, unknown>, keys: readonly string[], what: string): void {
  const actual = Reflect.ownKeys(value);
  const missing = keys.filter((key) => !Object.hasOwn(value, key));
  if (missing.length > 0) {
    throw new Error(`Invalid ${what}: missing ${missing.join(', ')}`);
  }
  const unexpected = actual.filter((key) => !keys.includes(key as string));
  if (unexpected.length > 0) {
    throw new Error(`Invalid ${what}: unexpected ${unexpected.join(', ')}`);
  }
}

function text(value: unknown, what: string): string {
  if (typeof value !== 'string') throw new Error(`Invalid ${what}: expected a string`);
  return value;
}

function nullableText(value: unknown, what: string): string | null {
  if (value === null) return null;
  return text(value, what);
}

function count(value: unknown, what: string): number {
  if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) {
    throw new Error(`Invalid ${what}: expected a non-negative integer`);
  }
  return value;
}

function positive(value: unknown, what: string): number {
  const parsed = count(value, what);
  if (parsed < 1) throw new Error(`Invalid ${what}: expected a positive integer`);
  return parsed;
}

function list(value: unknown, what: string): readonly unknown[] {
  if (!Array.isArray(value)) throw new Error(`Invalid ${what}: expected an array`);
  return value;
}

function parseClaims(value: unknown): ClaimsView {
  const claims = record(value, 'claims');
  exactKeys(
    claims,
    [
      'modelName',
      'category',
      'manufacturerClaims',
      'source',
      'digest',
      ...(Object.hasOwn(claims, 'contentJson') ? ['contentJson'] : []),
    ],
    'claims',
  );
  return {
    modelName: text(claims.modelName, 'claims.modelName'),
    category: text(claims.category, 'claims.category'),
    manufacturerClaims: text(claims.manufacturerClaims, 'claims.manufacturerClaims'),
    source: text(claims.source, 'claims.source'),
    ...(Object.hasOwn(claims, 'contentJson')
      ? { contentJson: text(claims.contentJson, 'claims.contentJson') }
      : {}),
    digest: text(claims.digest, 'claims.digest'),
  };
}

function parseSubmission(value: unknown): SubmissionView {
  const submission = record(value, 'submission');
  exactKeys(
    submission,
    ['productId', 'country', 'supplier', 'revision', 'digest', 'makerAccount', 'submittedAt'],
    'submission',
  );
  return {
    productId: text(submission.productId, 'submission.productId'),
    country: text(submission.country, 'submission.country'),
    supplier: text(submission.supplier, 'submission.supplier'),
    revision: positive(submission.revision, 'submission.revision'),
    digest: text(submission.digest, 'submission.digest'),
    makerAccount: text(submission.makerAccount, 'submission.makerAccount'),
    submittedAt: text(submission.submittedAt, 'submission.submittedAt'),
  };
}

function parsePublication(value: unknown): PublicationView {
  const publication = record(value, 'publication');
  exactKeys(
    publication,
    [
      'productId',
      'country',
      'supplier',
      'publishedVersion',
      'digest',
      'submissionRevision',
      'decisionId',
      'publisherAccount',
      'publishedAt',
    ],
    'publication',
  );
  return {
    productId: text(publication.productId, 'publication.productId'),
    country: text(publication.country, 'publication.country'),
    supplier: text(publication.supplier, 'publication.supplier'),
    publishedVersion: positive(publication.publishedVersion, 'publication.publishedVersion'),
    digest: text(publication.digest, 'publication.digest'),
    submissionRevision: positive(publication.submissionRevision, 'publication.submissionRevision'),
    decisionId: text(publication.decisionId, 'publication.decisionId'),
    publisherAccount: text(publication.publisherAccount, 'publication.publisherAccount'),
    publishedAt: text(publication.publishedAt, 'publication.publishedAt'),
  };
}

function productState(value: unknown): ProductModelView['state'] {
  if (value !== 'DRAFT' && value !== 'SUBMITTED' && value !== 'PUBLISHED') {
    throw new Error('Invalid product state');
  }
  return value;
}

export function parseProductModel(value: unknown): ProductModelView {
  const product = record(value, 'product model');
  exactKeys(
    product,
    [
      'productId',
      'country',
      'supplier',
      'version',
      'state',
      'claims',
      'submissions',
      'publications',
      'returnDecisionId',
    ],
    'product model',
  );
  return {
    productId: text(product.productId, 'product.productId'),
    country: text(product.country, 'product.country'),
    supplier: text(product.supplier, 'product.supplier'),
    version: positive(product.version, 'product.version'),
    state: productState(product.state),
    claims: parseClaims(product.claims),
    submissions: list(product.submissions, 'product.submissions').map(parseSubmission),
    publications: list(product.publications, 'product.publications').map(parsePublication),
    returnDecisionId: nullableText(product.returnDecisionId, 'product.returnDecisionId'),
  };
}

export function parseSubmissionEnvelope(value: unknown): SubmissionView {
  // The submit route answers with the immutable revision it created, in the SAME shape it uses
  // inside a product. Parsing it with the same function is what keeps the two from drifting.
  return parseSubmission(value);
}

export function parsePublicationEnvelope(value: unknown): PublicationView {
  return parsePublication(value);
}

function parseDecision(value: unknown): ReviewCaseView['decision'] {
  if (value === null) return null;
  const decision = record(value, 'decision');
  exactKeys(
    decision,
    ['decisionId', 'outcome', 'reason', 'checkerAccount', 'decidedAt'],
    'decision',
  );
  const outcome = decision.outcome;
  if (outcome !== 'ACCEPTED' && outcome !== 'CHANGES_REQUESTED') {
    throw new Error('Invalid decision outcome');
  }
  return {
    decisionId: text(decision.decisionId, 'decision.decisionId'),
    outcome,
    reason: text(decision.reason, 'decision.reason'),
    checkerAccount: text(decision.checkerAccount, 'decision.checkerAccount'),
    decidedAt: text(decision.decidedAt, 'decision.decidedAt'),
  };
}

function parseObservation(value: unknown): ReviewCaseView['observation'] {
  if (value === null) return null;
  const observation = record(value, 'observation');
  exactKeys(
    observation,
    [
      'ownerStatus',
      'ownerPublishedVersion',
      'readbackStatus',
      'readbackPublishedVersion',
      'exactReadbackConfirmed',
      'observedAt',
    ],
    'observation',
  );
  if (typeof observation.exactReadbackConfirmed !== 'boolean') {
    throw new Error('Invalid observation.exactReadbackConfirmed');
  }
  const ownerVersion = observation.ownerPublishedVersion;
  const readbackVersion = observation.readbackPublishedVersion;
  return {
    ownerStatus: text(observation.ownerStatus, 'observation.ownerStatus'),
    ownerPublishedVersion:
      ownerVersion === null ? null : positive(ownerVersion, 'observation.ownerPublishedVersion'),
    readbackStatus: text(observation.readbackStatus, 'observation.readbackStatus'),
    readbackPublishedVersion:
      readbackVersion === null
        ? null
        : positive(readbackVersion, 'observation.readbackPublishedVersion'),
    // Passed through, NOT recomputed. Recomputing "did the readback match" here would be a second
    // place for the three-facts rule to be wrong, and the Owner is the authority on it.
    exactReadbackConfirmed: observation.exactReadbackConfirmed,
    observedAt: text(observation.observedAt, 'observation.observedAt'),
  };
}

export function parseReviewCase(value: unknown): ReviewCaseView {
  const reviewCase = record(value, 'review case');
  exactKeys(
    reviewCase,
    ['caseId', 'version', 'submitted', 'decision', 'observation'],
    'review case',
  );
  const submitted = record(reviewCase.submitted, 'review case submitted');
  const contentKeys = ['immutableContent', 'sourceEvidence'];
  const hasContent = contentKeys.some((key) => Object.hasOwn(submitted, key));
  exactKeys(
    submitted,
    [
      // The typed subject identity. `productId` and `supplier` are still required alongside them:
      // this parser serves the 06/08 product surface, whose confirmed consumers read those names,
      // and a response that carried only the typed names would be a wire change rather than a
      // widening. The server emits both from the same fields, so they cannot disagree.
      'subjectKind',
      'subjectId',
      'ownerScope',
      'productId',
      'revision',
      'digest',
      'country',
      'supplier',
      'reviewScope',
      'makerAccount',
      ...(hasContent ? contentKeys : []),
    ],
    'review case submitted',
  );
  const subjectKind = text(submitted.subjectKind, 'submitted.subjectKind');
  if (subjectKind !== 'PRODUCT_MODEL' && subjectKind !== 'LOCAL_ASSET') {
    throw new Error('Invalid submitted.subjectKind');
  }
  return {
    caseId: text(reviewCase.caseId, 'reviewCase.caseId'),
    version: positive(reviewCase.version, 'reviewCase.version'),
    submitted: {
      subjectKind,
      subjectId: text(submitted.subjectId, 'submitted.subjectId'),
      ownerScope: text(submitted.ownerScope, 'submitted.ownerScope'),
      productId: text(submitted.productId, 'submitted.productId'),
      revision: positive(submitted.revision, 'submitted.revision'),
      digest: text(submitted.digest, 'submitted.digest'),
      country: text(submitted.country, 'submitted.country'),
      supplier: text(submitted.supplier, 'submitted.supplier'),
      reviewScope: text(submitted.reviewScope, 'submitted.reviewScope'),
      makerAccount: text(submitted.makerAccount, 'submitted.makerAccount'),
      immutableContent: hasContent
        ? text(submitted.immutableContent, 'submitted.immutableContent')
        : null,
      sourceEvidence: hasContent
        ? text(submitted.sourceEvidence, 'submitted.sourceEvidence')
        : null,
    },
    decision: parseDecision(reviewCase.decision),
    observation: parseObservation(reviewCase.observation),
  };
}

export function parseReviewCaseEnvelope(value: unknown): ReviewCaseView {
  const envelope = record(value, 'review case envelope');
  exactKeys(envelope, ['status', 'reviewCase'], 'review case envelope');
  const status = text(envelope.status, 'review case status');
  if (status !== 'FOUND') throw new Error('Invalid review case envelope status');
  return parseReviewCase(envelope.reviewCase);
}

export function parseExactReadback(value: unknown): ExactReadbackView {
  const envelope = record(value, 'exact readback');
  exactKeys(envelope, ['status', 'publication'], 'exact readback');
  const status = envelope.status;
  if (
    status !== 'FOUND' &&
    status !== 'UNKNOWN' &&
    status !== 'MISMATCH' &&
    status !== 'UNAVAILABLE'
  ) {
    throw new Error('Invalid exact readback status');
  }
  const publication = envelope.publication === null ? null : parsePublication(envelope.publication);
  // The shape rule the server itself enforces: FOUND carries a publication and nothing else does.
  // Checking it here means a client can never render a confirmed publication out of a mismatch.
  if ((status === 'FOUND') !== (publication !== null)) {
    throw new Error('Invalid exact readback: status and publication disagree');
  }
  return { status, publication };
}

/**
 * Reads the server's error body.
 *
 * An error body is a FIXED LITERAL from the server: `{"error":"CODE"}` or
 * `{"error":"CODE","field":"name"}`. It never echoes the request, the session or a value that was
 * sent, so there is nothing here to sanitise — and if the server ever did echo something, this
 * parser would reject the shape rather than display it.
 */
export function parseServerError(value: unknown): { code: string; field: string | null } {
  const body = record(value, 'error body');
  const keys = Reflect.ownKeys(body);
  exactKeys(body, keys.includes('field') ? ['error', 'field'] : ['error'], 'error body');
  return {
    code: text(body.error, 'error'),
    field: Object.hasOwn(body, 'field') ? text(body.field, 'error field') : null,
  };
}
